import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionLabel } from "./sharedUI";

// The Castle Groundcare case study — real client, live site screenshot,
// autoscrolling browser frame + brand elements + real mobile preview.
export const OurWorkSection = () => (
  <section id="our-work" className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]" data-testid="our-work">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <SectionLabel n="02" name="Our Work / Live client · Case study 01" />

      <div className="grid grid-cols-12 gap-8 lg:gap-12 items-start mb-10 lg:mb-14">
        <div className="col-span-12 lg:col-span-7">
          <p className="grid-ref mb-3">Trade — Landscaping &amp; groundcare · Essex</p>
          <h2 className="h-lg text-[color:var(--ink)]">
            Castle Groundcare — Essex landscaping, <span className="font-serif-italic">properly online</span>.
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <p className="grid-ref mb-3">Brief</p>
          <p className="text-[15px] leading-[1.6] text-[color:var(--muted)] max-w-md">
            Nick's a landscaper with a decade of work behind him — but his business was living on word-of-mouth and a phone number. We built Castle Groundcare a full brand and a five-page website that quotes higher-end residential and estate work from Great Dunmow across all of Essex.
          </p>
          <a
            href="https://castle-groundcare.emergent.host/"
            target="_blank"
            rel="noreferrer"
            className="btn-navy mt-5 inline-flex text-[13px]"
            data-testid="castle-visit-link"
          >
            Visit castlegroundcare.co.uk <ArrowRight size={13} />
          </a>
        </div>
      </div>

      {/* Big editorial visual — asymmetric split */}
      <div className="grid grid-cols-12 gap-4">
        {/* Big website screenshot — 8 cols */}
        <div className="col-span-12 md:col-span-8 relative">
          <div className="relative rounded-sm overflow-hidden aspect-[16/10] bg-[#0B1C2E] p-3 md:p-4 shadow-[0_30px_60px_-30px_rgba(11,28,46,0.45)] group" data-testid="castle-frame-hover-container">
            {/* macOS-style browser chrome */}
            <div className="bg-white rounded-md overflow-hidden h-full flex flex-col border border-black/10" data-testid="castle-browser-frame">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#EDEDED] border-b border-black/10">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white border border-black/10 rounded-md px-3 py-0.5 text-[10px] md:text-[11px] text-[color:var(--muted)] font-medium tracking-wide flex items-center gap-1.5 max-w-[280px] truncate">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    castlegroundcare.co.uk
                  </div>
                </div>
                <div className="w-[54px]" />
              </div>
              {/* Autoscroll viewport — tall image scrolls up on hover */}
              <div className="flex-1 overflow-hidden bg-[#1F4632] relative">
                <img
                  src="/castle-groundcare-home.jpg"
                  alt="Castle Groundcare homepage — a website built by ReadyTrades"
                  loading="lazy"
                  className="w-full absolute top-0 left-0 castle-autoscroll"
                />
                <span className="absolute bottom-2 right-2 z-20 text-[9px] font-semibold uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  Scrolling preview
                </span>
              </div>
            </div>
            <div className="absolute top-[52px] left-6 md:top-[60px] md:left-7 flex items-center gap-2 z-10">
              <span className="stamp stamp-light">01 · Website</span>
            </div>
            <div className="absolute bottom-6 left-6 right-6 md:bottom-7 md:left-7 md:right-7 flex items-end justify-between gap-3 text-white z-10 pointer-events-none">
              <div className="bg-[color:var(--ink)]/85 backdrop-blur-sm rounded-sm px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest opacity-70">Home page hero</p>
                <p className="font-display text-[16px] md:text-[20px] leading-tight mt-0.5">Professional landscaping in Essex.</p>
              </div>
              <span className="grid-ref text-white/85 bg-[color:var(--ink)]/70 px-2 py-1 rounded-sm whitespace-nowrap self-end">B · Live site</span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-[color:var(--muted)] hidden md:block">
            <span className="grid-ref">Hover</span> to preview the full page →
          </p>
        </div>

        {/* Right stack — 4 cols */}
        <div className="col-span-12 md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
          {/* Logo panel */}
          <div className="bg-[#1F4632] rounded-sm p-6 md:p-8 flex flex-col justify-between aspect-square md:aspect-auto md:min-h-[220px] relative overflow-hidden">
            <span className="grid-ref text-white/60 absolute top-4 left-4">02 · Logo</span>
            <div className="flex items-center justify-center flex-1">
              <div className="text-center">
                <p className="font-display text-white text-[26px] md:text-[32px] leading-none">Castle</p>
                <p className="text-white/70 text-[10px] tracking-[0.3em] uppercase mt-2">Groundcare · Est. 2015</p>
              </div>
            </div>
          </div>

          {/* Palette panel */}
          <div className="bg-[color:var(--bg-paper)] border border-[color:var(--border-soft)] rounded-sm p-6 md:p-8 relative">
            <span className="grid-ref absolute top-4 left-4">03 · Palette</span>
            <div className="mt-8 grid grid-cols-4 gap-1.5">
              {[
                { c: "#1F4632", n: "Forest" },
                { c: "#5C6C4E", n: "Moss" },
                { c: "#D8CDB7", n: "Stone" },
                { c: "#F5F1EA", n: "Cream" },
              ].map((sw) => (
                <div key={sw.c}>
                  <div className="aspect-square rounded-sm border border-[color:var(--border-soft)]" style={{ background: sw.c }} />
                  <p className="text-[9px] mt-1 font-medium text-[color:var(--muted)] uppercase tracking-widest">{sw.n}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Second row — mobile screen + services + direction */}
      <div className="grid grid-cols-12 gap-4 mt-4">
        {/* Phone — real Castle mobile view */}
        <div className="col-span-12 md:col-span-4 bg-[color:var(--bg-paper)] border border-[color:var(--border-soft)] rounded-sm p-6 md:p-8 flex flex-col relative" data-testid="castle-mobile-panel">
          <span className="grid-ref absolute top-4 left-4">04 · Mobile</span>
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="w-[150px] rounded-[22px] bg-[#0A0A0A] p-[5px] shadow-[0_20px_40px_-15px_rgba(11,28,46,0.5)]">
              <div className="relative rounded-[18px] overflow-hidden bg-white">
                <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-3 bg-black rounded-full z-20" />
                <div className="aspect-[9/19]">
                  <img
                    src="/castle-groundcare-mobile.jpg"
                    alt="Castle Groundcare mobile view"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services & stats — 4 cols */}
        <div className="col-span-12 md:col-span-4 bg-[color:var(--bg-paper)] border border-[color:var(--border-soft)] rounded-sm p-6 md:p-8 relative">
          <span className="grid-ref absolute top-4 left-4">05 · Services</span>
          <div className="mt-8">
            <ul className="space-y-2 text-[13px] text-[color:var(--ink)]">
              {[
                "Lawn care & maintenance",
                "Garden design & planting",
                "Patio & decking",
                "Hedge trimming & tree work",
                "Fencing",
              ].map((s, i) => (
                <li key={s} className="flex items-center gap-3">
                  <span className="font-display tabular text-[#1F4632] text-[13px]">0{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-[color:var(--border-soft)] grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="font-display text-[color:var(--ink)] text-[18px]">10+</p>
                <p className="text-[9px] text-[color:var(--muted)] uppercase tracking-widest mt-0.5">Years</p>
              </div>
              <div>
                <p className="font-display text-[color:var(--ink)] text-[18px]">500+</p>
                <p className="text-[9px] text-[color:var(--muted)] uppercase tracking-widest mt-0.5">Clients</p>
              </div>
              <div>
                <p className="font-display text-[color:var(--ink)] text-[18px]">100%</p>
                <p className="text-[9px] text-[color:var(--muted)] uppercase tracking-widest mt-0.5">Insured</p>
              </div>
            </div>
          </div>
        </div>

        {/* Direction note — 4 cols */}
        <div className="col-span-12 md:col-span-4 bg-[color:var(--ink)] text-white rounded-sm p-6 md:p-8 relative">
          <span className="grid-ref text-white/60 absolute top-4 left-4">06 · Direction</span>
          <div className="mt-8">
            <p className="font-display text-[20px] lg:text-[22px] leading-tight text-white">
              Considered, dependable, distinctly Essex. A deep-forest palette and <span className="font-serif-italic text-[color:var(--accent)]">real photography</span> of Nick's own work to position Castle for higher-value residential and estate contracts.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <span className="stamp stamp-light">Built</span>
              <span className="stamp stamp-light">Live</span>
              <span className="grid-ref text-white/50 ml-auto">Ref. CG-01</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 lg:mt-14 flex items-center justify-between border-t border-[color:var(--border-soft)] pt-6">
        <p className="grid-ref">— More ReadyTrades client work</p>
        <Link to="/our-clients" className="btn-ghost text-[14px]" data-testid="our-work-cta">
          See all clients <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </section>
);
